import { ParsedUrlQuery, parse } from 'querystring';

export interface ExtTalentBrandDealAttribution {
  actionId?: string;
  contactId?: string;
  campaignId: string;
  extTalentId: string;
}

const EXT_TALENT_BD_CHANNEL = 'bd';
export const EXT_TALENT_BD_STORAGE_KEY = 'ext-talent-bd-attribution';
export const EXT_TALENT_BD_RETURN_URL_KEY = 'ext-talent-bd-return-url';

/**
 * A visitor who clicks a brand-deal link may take days to decide. Longer than
 * this and the click is stale enough that it must not outlive the campaign
 * and hijack an unrelated later signup (e.g. on the claim-landing page).
 */
const EXT_TALENT_BD_ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const firstNonEmptyString = (
  value: string | string[] | undefined
): string | undefined => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const getExtTalentBrandDealAttributionFromQuery = (
  query: ParsedUrlQuery
): ExtTalentBrandDealAttribution | null => {
  const channel = firstNonEmptyString(query.c);
  if (channel !== EXT_TALENT_BD_CHANNEL) return null;

  const campaignId = firstNonEmptyString(query.caid);
  const extTalentId = firstNonEmptyString(query.tid);
  if (!campaignId || !extTalentId) return null;

  const actionId = firstNonEmptyString(query.aid);
  const contactId = firstNonEmptyString(query.cid);

  return {
    campaignId,
    extTalentId,
    ...(actionId && { actionId }),
    ...(contactId && { contactId }),
  };
};

export const setExtTalentBrandDealAttributionToStorage = (
  attribution: ExtTalentBrandDealAttribution
): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    EXT_TALENT_BD_STORAGE_KEY,
    JSON.stringify({ ...attribution, savedAt: Date.now() })
  );
};

export const getExtTalentBrandDealAttributionFromStorage = (): ExtTalentBrandDealAttribution | null => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(EXT_TALENT_BD_STORAGE_KEY);
  if (!raw) return null;
  try {
    const { savedAt, ...parsed } = JSON.parse(
      raw
    ) as ExtTalentBrandDealAttribution & {
      savedAt?: number;
    };
    if (!parsed?.campaignId || !parsed?.extTalentId) return null;
    if (savedAt && Date.now() - savedAt > EXT_TALENT_BD_ATTRIBUTION_TTL_MS) {
      clearExtTalentBrandDealAttributionFromStorage();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearExtTalentBrandDealAttributionFromStorage = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(EXT_TALENT_BD_STORAGE_KEY);
};

/**
 * The campaign invite link itself, kept separately from the generic `redirect`
 * key: a social provider round-trip replaces the URL with its own callback, and
 * `redirect` is shared with every other flow that can overwrite it in between.
 */
export const clearExtTalentBdReturnUrlFromStorage = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(EXT_TALENT_BD_RETURN_URL_KEY);
};

/**
 * A provider round-trip takes seconds. Anything older than this was abandoned
 * at the provider, and must not hijack an unrelated social login later on.
 */
const EXT_TALENT_BD_RETURN_URL_TTL_MS = 30 * 60 * 1000;

export const setExtTalentBdReturnUrlToStorage = (url: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    EXT_TALENT_BD_RETURN_URL_KEY,
    JSON.stringify({ url, savedAt: Date.now() })
  );
};

/**
 * Call when a social provider is chosen. Only a campaign invite link is kept,
 * so an ordinary social login started anywhere else is untouched.
 */
export const rememberExtTalentBdReturnUrl = (url: string): void => {
  const [, queryString = ''] = url.split('?');
  const attribution = getExtTalentBrandDealAttributionFromQuery(
    parse(queryString)
  );

  if (!attribution) return;

  setExtTalentBdReturnUrlToStorage(url);
};

export const getExtTalentBdReturnUrlFromStorage = (): string | null => {
  if (typeof window === 'undefined') return null;

  const raw = localStorage.getItem(EXT_TALENT_BD_RETURN_URL_KEY);
  if (!raw) return null;

  try {
    const { url, savedAt } = JSON.parse(raw) as {
      url?: string;
      savedAt?: number;
    };

    if (!url || !savedAt) return null;

    if (Date.now() - savedAt > EXT_TALENT_BD_RETURN_URL_TTL_MS) {
      clearExtTalentBdReturnUrlFromStorage();
      return null;
    }

    return url;
  } catch {
    return null;
  }
};
